"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CurrencyField,
  type FrequencyLabels,
  useMoney,
} from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import {
  aprTable,
  apyTable,
  type CompoundingKey,
  perYearFor,
  RATE_FREQUENCIES,
} from "@/lib/tool-kit/finance/rates";
import {
  CONTINUOUS,
  effectiveAnnualRate,
  nominalFromEffective,
} from "@/lib/tool-kit/finance/tvm";

/** English strings. A translated content file overrides these through `ui`. */
export const APR_TO_APY_CONVERTER_STRINGS = {
  inputsHeading: "Your rate",
  direction: "Convert",
  aprToApy: "APR to APY",
  apyToApr: "APY to APR",
  aprRate: "Nominal annual rate, APR (%)",
  apyRate: "Effective annual rate, APY (%)",
  compounding: "Interest is compounded",
  annually: "Annually (1 per year)",
  semiannually: "Semiannually (2 per year)",
  quarterly: "Quarterly (4 per year)",
  bimonthly: "Every two months (6 per year)",
  monthly: "Monthly (12 per year)",
  semimonthly: "Twice a month (24 per year)",
  biweekly: "Every two weeks (26 per year)",
  weekly: "Weekly (52 per year)",
  daily: "Daily (365 per year)",
  continuous: "Continuously",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  balance: "Balance for the example",
  resultsHeading: "Results",
  apy: "Effective annual rate (APY)",
  apr: "Nominal annual rate (APR)",
  periodicRate: "Rate per compounding period",
  difference: "Difference between APY and APR",
  oneYear: "Interest after one year",
  workingHeading: "The working",
  workApy: "APY = (1 + APR ÷ m)^m − 1 = (1 + {apr} ÷ {m})^{m} − 1 = {apy}%",
  workApyContinuous: "APY = e^APR − 1 = e^{apr} − 1 = {apy}%",
  workApr:
    "APR = m × [(1 + APY)^(1 ÷ m) − 1] = {m} × [(1 + {apy})^(1 ÷ {m}) − 1] = {aprPct}%",
  workAprContinuous: "APR = ln(1 + APY) = ln(1 + {apy}) = {aprPct}%",
  tableApy: "The same APR at every compounding frequency",
  tableApr: "The same APY as an APR at every compounding frequency",
  frequencyColumn: "Compounding",
  timesColumn: "Times per year",
  aprColumn: "APR",
  apyColumn: "APY",
  infinite: "∞",
  note: "This converts between a nominal and an effective annual rate and includes no fees. A loan's regulatory APR can include fees, so it is not always the plain nominal rate.",
  invalidRate: "Enter a rate of 0 or more (and an APY above −100%).",
} as const;

type Strings = typeof APR_TO_APY_CONVERTER_STRINGS;
type Direction = "aprToApy" | "apyToApr";

function WorkingLine({
  direction,
  rate,
  perYear,
  result,
  strings,
}: {
  direction: Direction;
  rate: number;
  perYear: number;
  result: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const num = (v: number) => fmt(v, { maximumFractionDigits: 8 });
  const pct = (v: number) => fmt(v * 100, { maximumFractionDigits: 6 });
  const continuous = perYear === CONTINUOUS;
  let template: string = strings.workApy;
  if (direction === "aprToApy") {
    template = continuous ? strings.workApyContinuous : strings.workApy;
  } else {
    template = continuous ? strings.workAprContinuous : strings.workApr;
  }
  const values: Record<string, string> =
    direction === "aprToApy"
      ? { apr: num(rate), m: fmt(perYear), apy: pct(result) }
      : { apy: num(rate), m: fmt(perYear), aprPct: pct(result) };
  return (
    <p className="font-mono text-muted-foreground text-sm leading-relaxed">
      {t(template, values)}
    </p>
  );
}

function FrequencyTable({
  direction,
  rate,
  strings,
}: {
  direction: Direction;
  rate: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 4 })}%`;
  const rows = direction === "aprToApy" ? apyTable(rate) : aprTable(rate);
  const fixed = pct(rate);
  return (
    <DataTable
      head={[
        strings.frequencyColumn,
        strings.timesColumn,
        direction === "aprToApy" ? strings.aprColumn : strings.apyColumn,
        direction === "aprToApy" ? strings.apyColumn : strings.aprColumn,
      ]}
      rows={rows.map((row) => [
        strings[row.key],
        row.perYear === CONTINUOUS ? strings.infinite : fmt(row.perYear),
        fixed,
        pct(row.rate),
      ])}
    />
  );
}

function Results({
  direction,
  rate,
  perYear,
  currency,
  balance,
  strings,
}: {
  direction: Direction;
  rate: number;
  perYear: number;
  currency: string;
  balance: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const converted =
    direction === "aprToApy"
      ? effectiveAnnualRate(rate, perYear)
      : nominalFromEffective(rate, perYear);
  const apr = direction === "aprToApy" ? rate : converted;
  const apy = direction === "aprToApy" ? converted : rate;
  const pct = (v: number, digits = 6) =>
    `${fmt(v * 100, { maximumFractionDigits: digits })}%`;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={direction === "aprToApy" ? strings.apy : strings.apr}
          value={pct(converted)}
        />
        <StatCard
          label={direction === "aprToApy" ? strings.apr : strings.apy}
          value={pct(rate)}
        />
        {perYear === CONTINUOUS ? null : (
          <StatCard label={strings.periodicRate} value={pct(apr / perYear)} />
        )}
        <StatCard label={strings.difference} value={pct(apy - apr)} />
        {balance > 0 ? (
          <StatCard label={strings.oneYear} value={money(balance * apy)} />
        ) : null}
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <WorkingLine
          direction={direction}
          perYear={perYear}
          rate={rate}
          result={converted}
          strings={strings}
        />
      </div>
      <h3 className={WIDGET_H3}>
        {direction === "aprToApy" ? strings.tableApy : strings.tableApr}
      </h3>
      <FrequencyTable direction={direction} rate={rate} strings={strings} />
      <p className={HINT}>{strings.note}</p>
    </section>
  );
}

export default function AprToApyConverterWidget() {
  const strings = useToolUi(APR_TO_APY_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("aprToApy");
  const [rateText, setRateText] = useState("6");
  const [frequency, setFrequency] = useState<CompoundingKey>("monthly");
  const [currency, setCurrency] = useState("USD");
  const [balanceText, setBalanceText] = useState("10000");

  const percent = parseNumberInput(rateText);
  const valid =
    percent !== null &&
    (direction === "aprToApy" ? percent >= 0 : percent > -100);
  const labels: FrequencyLabels = strings;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <PillGroup
          label={strings.direction}
          name="aa-direction"
          onChange={setDirection}
          options={[
            { value: "aprToApy", label: strings.aprToApy },
            { value: "apyToApr", label: strings.apyToApr },
          ]}
          value={direction}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="aa-rate"
            label={direction === "aprToApy" ? strings.aprRate : strings.apyRate}
            numeric
            onChange={setRateText}
            value={rateText}
          />
          <SelectField
            id="aa-compounding"
            label={strings.compounding}
            onChange={setFrequency}
            options={RATE_FREQUENCIES.map((key) => ({
              value: key,
              label: labels[key],
            }))}
            value={frequency}
          />
          <CurrencyField
            id="aa-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            id="aa-balance"
            label={strings.balance}
            numeric
            onChange={setBalanceText}
            value={balanceText}
          />
        </div>
      </section>
      {valid ? (
        <Results
          balance={Math.max(0, parseNumberOr(balanceText, 0))}
          currency={currency}
          direction={direction}
          perYear={perYearFor(frequency)}
          rate={percent / 100}
          strings={strings}
        />
      ) : (
        <Notice>{strings.invalidRate}</Notice>
      )}
    </div>
  );
}
